/**
 * Bridge to the iOS / Android app shell (Capacitor), when the web app runs
 * inside one. Everything here is a no-op in a browser tab.
 *
 * The plugins are reached through the `window.Capacitor` global the native
 * shell injects, so the web bundle carries no Capacitor dependency; they
 * light up once the native project ships @capacitor/filesystem and
 * @capacitor/share (see docs/mobile-app.md).
 */

type CapacitorPlugins = {
  Filesystem?: {
    writeFile: (opts: { path: string; data: string; directory: string; recursive?: boolean }) => Promise<{ uri: string }>;
  };
  Share?: {
    share: (opts: { title?: string; dialogTitle?: string; files?: string[]; url?: string }) => Promise<unknown>;
  };
  Browser?: {
    open: (opts: { url: string }) => Promise<void>;
  };
  App?: {
    addListener: (event: 'backButton', listener: (event: { canGoBack: boolean }) => void) => unknown;
    minimizeApp?: () => Promise<void>;
  };
};

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: CapacitorPlugins;
};

function capacitor(): CapacitorGlobal | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor ?? null;
}

/** True inside the iOS/Android app shell, false in any browser. */
export function isNativeApp(): boolean {
  return !!capacitor()?.isNativePlatform?.();
}

/** Which app shell this runs in; null in a browser. */
export function nativePlatform(): 'ios' | 'android' | null {
  if (!isNativeApp()) return null;
  const platform = capacitor()?.getPlatform?.();
  return platform === 'ios' || platform === 'android' ? platform : null;
}

/** An open dialog, sheet, menu or popover: what Android's back button closes first. */
const OPEN_LAYER = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
  '[data-radix-popper-content-wrapper]',
].join(', ');

/**
 * Android's back button. Without a handler it leaves the page, or closes the
 * app, even while a dialog is open over the designer. With this one it closes
 * the topmost layer the way Escape does, then goes back a page, and on the
 * first page puts the app in the background instead of quitting it.
 */
export function installAndroidBackButton(): void {
  const app = nativePlatform() === 'android' ? capacitor()?.Plugins?.App : undefined;
  if (!app?.addListener) return;
  app.addListener('backButton', ({ canGoBack }) => {
    if (document.querySelector(OPEN_LAYER)) {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true }));
      return;
    }
    if (canGoBack) window.history.back();
    else void app.minimizeApp?.();
  });
}

export const WEBSITE_ORIGIN = 'https://cctvplanner.io';

/** Opens a page in the system browser (an in-app Safari sheet on iOS). */
export async function openInSystemBrowser(url: string): Promise<void> {
  const browser = capacitor()?.Plugins?.Browser;
  if (browser) await browser.open({ url });
  else window.open(url, '_blank', 'noopener');
}

/**
 * In the app, a link to a page the app does not serve (blog, guides,
 * landing pages) opens on the website in the system browser instead of being
 * bounced back to the dashboard. Links to other hosts already leave the app.
 */
export function installNativeLinkPolicy(isAppPage: (pathname: string) => boolean): void {
  if (!isNativeApp()) return;
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!anchor || anchor.hasAttribute('download')) return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin || isAppPage(url.pathname)) return;
    // Capture phase: runs before React Router's own click handling.
    event.preventDefault();
    event.stopPropagation();
    void openInSystemBrowser(`${WEBSITE_ORIGIN}${url.pathname}${url.search}${url.hash}`);
  }, true);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Writes the file to the app's cache and opens the system share sheet, where
 * the user saves it to Files, sends it by mail or AirDrops it. A WebView has
 * no download manager, so this is how a PDF or CSV leaves the app.
 */
export async function shareFileFromApp(blob: Blob, filename: string): Promise<void> {
  const plugins = capacitor()?.Plugins;
  if (!plugins?.Filesystem || !plugins.Share) throw new Error('Filesystem/Share plugins missing');
  const safeName = filename.replace(/[\\/:*?"<>|]+/g, '_') || 'download';
  const data = await blobToBase64(blob);
  const { uri } = await plugins.Filesystem.writeFile({ path: safeName, data, directory: 'CACHE', recursive: true });
  await plugins.Share.share({ title: safeName, dialogTitle: safeName, files: [uri] });
}

let downloadBridgeInstalled = false;

/**
 * Every export in the app ends in a programmatic click on an `<a download>`
 * pointing at a blob: or data: URL. Inside the app shell such a click does
 * nothing, so it is routed to the share sheet instead. One hook here instead
 * of a change at each of the export sites.
 */
export function installNativeDownloadBridge(): void {
  if (downloadBridgeInstalled || !isNativeApp()) return;
  downloadBridgeInstalled = true;
  const originalClick = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function nativeAwareClick(this: HTMLAnchorElement) {
    const filename = this.getAttribute('download');
    if (filename !== null && /^(blob|data):/.test(this.href)) {
      const href = this.href;
      void fetch(href)
        .then((res) => res.blob())
        .then((blob) => shareFileFromApp(blob, filename || 'download'))
        .catch(() => originalClick.call(this));
      return;
    }
    originalClick.call(this);
  };
}
